import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import {
  getTraineeGroups,
  createTraineeGroup,
  updateTraineeGroup,
  deleteTraineeGroup,
  getTraineesInGroup,
  addTraineesToGroup,
  removeTraineeFromGroup,
} from '../../../services/adminService';
import { getTraineeDetails } from '../../../services/traineeService';
import { toast } from 'react-toastify';

function TraineeGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingGroup, setEditingGroup] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Manage trainees inside a group
  const [manageGroup, setManageGroup] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [groupTrainees, setGroupTrainees] = useState([]);
  const [groupTraineesLoading, setGroupTraineesLoading] = useState(false);
  const [availableTrainees, setAvailableTrainees] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [traineeSearch, setTraineeSearch] = useState('');
  const [selectedTraineeIds, setSelectedTraineeIds] = useState([]);

  // Pagination for trainees INSIDE group
  const [groupTraineePage, setGroupTraineePage] = useState(1);
  const [groupTraineeTotal, setGroupTraineeTotal] = useState(0);
  const groupTraineeLimit = 15;

  // Pagination for AVAILABLE trainees (search results)
  const [availablePage, setAvailablePage] = useState(1);
  const [availableTotal, setAvailableTotal] = useState(0);
  const availableLimit = 15;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({ name: '', description: '' });

  // Pagination for main groups list (client-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await getTraineeGroups();
      if (response?.success && Array.isArray(response?.data?.groups)) {
        setGroups(response.data.groups);
      } else if (response?.success && Array.isArray(response?.data)) {
        setGroups(response.data);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error('Error fetching trainee groups:', error);
      setGroups([]);
      toast.error('Failed to load trainee groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const totalPages = Math.ceil(groups.length / itemsPerPage);
  const paginatedGroups = groups.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleOpenCreate = () => {
    setModalMode('create');
    setEditingGroup(null);
    setFormData({ name: '', description: '' });
    setFormErrors({ name: '', description: '' });
    setShowModal(true);
  };

  const handleOpenEdit = (group) => {
    setModalMode('edit');
    setEditingGroup(group);
    setFormData({
      name: group.name || '',
      description: group.description || '',
    });
    setFormErrors({ name: '', description: '' });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setFormData({ name: '', description: '' });
    setFormErrors({ name: '', description: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = 'Group name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
      };
      const response =
        modalMode === 'create'
          ? await createTraineeGroup(payload)
          : await updateTraineeGroup(editingGroup.id, payload);

      if (response?.success) {
        toast.success(
          modalMode === 'create'
            ? 'Trainee group created successfully'
            : 'Trainee group updated successfully'
        );
        fetchGroups();
        handleCloseModal();
      } else {
        toast.error(
          response?.data?.error_msg ||
            response?.message ||
            (modalMode === 'create' ? 'Failed to create group' : 'Failed to update group')
        );
      }
    } catch (error) {
      console.error('Error saving trainee group:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (group) => {
    setDeletingGroup(group);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingGroup) return;
    setIsDeleting(true);
    try {
      const response = await deleteTraineeGroup(deletingGroup.id);
      if (response?.success) {
        toast.success('Trainee group deleted successfully');
        fetchGroups();
        setShowDeleteModal(false);
        setDeletingGroup(null);
      } else {
        toast.error(
          response?.data?.error_msg || response?.message || 'Failed to delete group'
        );
      }
    } catch (error) {
      console.error('Error deleting trainee group:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const openManageGroup = async (group) => {
    setManageGroup(group);
    setShowManageModal(true);
    setSelectedTraineeIds([]);
    setGroupTraineePage(1);
    setAvailablePage(1);
    const currentMembers = await fetchGroupTrainees(group.id, 1);
    await fetchAvailableTrainees('', 1, currentMembers);
  };

  const fetchGroupTrainees = async (groupId, page = 1) => {
    setGroupTraineesLoading(true);
    try {
      const res = await getTraineesInGroup(groupId, { page: page, limit: groupTraineeLimit, search: '' });
      if (res?.success) {
        const trainees = res.data?.trainees || res.data || [];
        setGroupTrainees(trainees);
        setGroupTraineeTotal(res.data?.pagination?.total || (res.data?.trainees?.length || 0));
        return trainees;
      } else {
        setGroupTrainees([]);
        setGroupTraineeTotal(0);
        return [];
      }
    } catch (error) {
      console.error('Error fetching group trainees:', error);
      setGroupTrainees([]);
      return [];
    } finally {
      setGroupTraineesLoading(false);
    }
  };

  const fetchAvailableTrainees = async (search, page = 1, currentGroupTrainees = null) => {
    setAvailableLoading(true);
    try {
      const res = await getTraineeDetails({
        page: page,
        limit: availableLimit,
        status: null,
        search: search || '',
      });

      if (res?.success) {
        const allTrainees = res.data?.data || res.data || [];
        // Filter out trainees who are already in the group
        const groupMembers = currentGroupTrainees || groupTrainees;
        const groupIds = groupMembers.map((t) => t.id);
        const filtered = allTrainees.filter((u) => !groupIds.includes(u.id));
        setAvailableTrainees(filtered);
        setAvailableTotal(res.data?.pagination?.totalRecords || 0);
      } else {
        setAvailableTrainees([]);
        setAvailableTotal(0);
      }
    } catch (error) {
      console.error('Error fetching available trainees:', error);
      setAvailableTrainees([]);
    } finally {
      setAvailableLoading(false);
    }
  };

  const toggleTraineeSelection = (id) => {
    setSelectedTraineeIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const handleAddSelectedTrainees = async () => {
    if (!manageGroup || selectedTraineeIds.length === 0) {
      toast.warning('Select at least one trainee to add.');
      return;
    }
    try {
      const res = await addTraineesToGroup(manageGroup.id, {
        userIds: selectedTraineeIds,
      });
      if (res?.success) {
        toast.success('Trainees added to group');
        setSelectedTraineeIds([]);
        const updatedGroup = await fetchGroupTrainees(manageGroup.id);
        await fetchAvailableTrainees(traineeSearch, availablePage, updatedGroup);
      } else {
        toast.error(res?.data?.error_msg || res?.message || 'Failed to add trainees');
      }
    } catch (error) {
      console.error('Error adding trainees to group:', error);
      toast.error('Something went wrong while adding trainees.');
    }
  };

  const handleRemoveTrainee = async (trainee) => {
    if (!manageGroup) return;
    try {
      const res = await removeTraineeFromGroup(manageGroup.id, trainee.id);
      if (res?.success) {
        toast.success('Trainee removed from group');
        const updatedGroup = await fetchGroupTrainees(manageGroup.id);
        await fetchAvailableTrainees(traineeSearch, availablePage, updatedGroup);
      } else {
        toast.error(res?.data?.error_msg || res?.message || 'Failed to remove trainee');
      }
    } catch (error) {
      console.error('Error removing trainee from group:', error);
      toast.error('Something went wrong while removing trainee.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="max-w-xl">
          <p className="text-gray-500 text-sm leading-relaxed">
            Organize your trainees into logical segments (e.g., Departments, Teams). Groups allow you to assign courses and manage permissions collectively for better efficiency.
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl bg-[#7083FF] text-white text-sm font-bold shadow-xl shadow-[#7083FF]/20 hover:bg-[#7083FF]/90 transition-all hover:scale-[1.02] active:scale-[0.98] w-full lg:w-auto"
          onClick={handleOpenCreate}
        >
          <Icon icon="fluent:add-24-regular" width={20} height={20} />
          Create New Group
        </button>
      </div>

      <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider w-20">#</th>
                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Group Name</th>
                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Description</th>
                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider text-center">Members</th>
                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center">
                        <div className="w-10 h-10 border-4 border-[#7083FF]/20 border-t-[#7083FF] rounded-full animate-spin mb-4" />
                        <p className="text-gray-500 font-medium">Loading groups...</p>
                    </div>
                  </td>
                </tr>
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                            <Icon icon="fluent:people-community-24-regular" className="text-gray-300 w-8 h-8" />
                        </div>
                        <h4 className="text-gray-900 font-bold">No Groups Found</h4>
                        <p className="text-gray-500 text-sm mt-1">Start by creating your first trainee group.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedGroups.map((group, index) => (
                  <tr key={group.id} className="hover:bg-gray-50/70 transition-colors group">
                    <td className="px-6 py-5 text-gray-400 font-medium">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td className="px-6 py-5">
                        <p className="font-bold text-gray-900">{group.name}</p>
                    </td>
                    <td className="px-6 py-5">
                        <p className="text-sm text-gray-600 truncate max-w-xs">{group.description || 'No description provided'}</p>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="inline-flex items-center justify-center px-3 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-bold ring-1 ring-indigo-200/50">
                        {group.user_count || 0} Members
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          className="p-2.5 rounded-xl text-gray-500 hover:text-[#7083FF] hover:bg-[#7083FF]/5 transition-all"
                          title="Manage Members"
                          onClick={() => openManageGroup(group)}
                        >
                          <Icon icon="fluent:people-community-24-regular" width="20" />
                        </button>
                        <button
                          type="button"
                          className="p-2.5 rounded-xl text-gray-500 hover:text-primary hover:bg-primary/5 transition-all"
                          title="Edit Group"
                          onClick={() => handleOpenEdit(group)}
                        >
                          <Icon icon="fluent:edit-24-regular" width="20" />
                        </button>
                        <button
                          type="button"
                          className="p-2.5 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all"
                          title="Delete Group"
                          onClick={() => handleDeleteClick(group)}
                        >
                          <Icon icon="fluent:delete-24-regular" width="20" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Container */}
      {!loading && groups.length > 0 && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-6 px-6 py-4 bg-white rounded-3xl border border-gray-100 shadow-sm">
          <div className="text-sm text-gray-500">
            Showing <span className="font-semibold text-gray-900">{(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, groups.length)}</span> of <span className="font-semibold text-gray-900">{groups.length}</span> groups
          </div>

          <div className="flex items-center gap-3">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
              className="px-3 py-2 rounded-xl border border-gray-100 bg-transparent text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/10"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>

            <div className="flex items-center gap-1 ml-4">
              <button
                type="button"
                className="p-2 rounded-xl border border-gray-100 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <Icon icon="fluent:chevron-left-24-regular" width="18" />
              </button>
              <div className="px-4 text-sm font-bold text-gray-900">
                Page {currentPage} <span className="text-gray-400 font-medium">/ {totalPages}</span>
              </div>
              <button
                type="button"
                className="p-2 rounded-xl border border-gray-100 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                <Icon icon="fluent:chevron-right-24-regular" width="18" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[24px] shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${modalMode === 'create' ? 'bg-[#7083FF]/10 text-[#7083FF]' : 'bg-indigo-600 text-white'}`}>
                      <Icon icon={modalMode === 'create' ? "fluent:add-24-regular" : "fluent:edit-24-regular"} width={20} />
                  </div>
                  <h5 className="text-base font-bold text-gray-900">
                    {modalMode === 'create' ? 'Create Trainee Group' : 'Edit Group Details'}
                  </h5>
              </div>
              <button
                type="button"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                onClick={handleCloseModal}
              >
                <Icon icon="fluent:dismiss-24-regular" width="18" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Group Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Sales Team, Engineering"
                  className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-4 focus:ring-[#7083FF]/10 transition-all text-sm font-medium ${
                    formErrors.name ? 'border-red-300 focus:border-red-400' : 'border-gray-100 focus:border-[#7083FF]/30'
                  }`}
                />
                {formErrors.name && (
                  <p className="text-red-500 text-xs mt-2 font-medium">{formErrors.name}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Description (Optional)</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="What is this group for?"
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#7083FF]/10 focus:border-[#7083FF]/30 transition-all text-sm font-medium resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 mt-4">
              <button
                type="button"
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-bold hover:bg-white transition-all"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2.5 rounded-xl bg-[#7083FF] text-white text-sm font-bold shadow-lg shadow-[#7083FF]/20 hover:bg-[#5f73e6] transition-all flex items-center gap-2"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {modalMode === 'create' ? 'Creating...' : 'Updating...'}
                  </>
                ) : (
                  modalMode === 'create' ? 'Create Group' : 'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteModal && deletingGroup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[24px] shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-6 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon icon="fluent:delete-24-regular" className="text-red-500 w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Group?</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Are you sure you want to delete <span className="font-bold text-gray-800">&quot;{deletingGroup.name}&quot;</span>? Users will be unassigned from this group.
                </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-bold hover:bg-white transition-all"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingGroup(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold shadow-lg shadow-red-100 hover:bg-red-600 transition-all flex items-center justify-center gap-2"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Group'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage trainees in group */}
      {showManageModal && manageGroup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[32px] shadow-2xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#7083FF] flex items-center justify-center text-white shadow-lg shadow-[#7083FF]/20">
                      <Icon icon="fluent:people-community-24-regular" width={24} />
                  </div>
                  <div>
                    <h5 className="text-xl font-bold text-gray-900 leading-tight">Manage Group Members</h5>
                    <p className="text-sm text-[#7083FF] font-semibold mt-0.5">{manageGroup.name}</p>
                  </div>
              </div>
              <button
                type="button"
                className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all"
                onClick={() => {
                  setShowManageModal(false);
                  setManageGroup(null);
                  setGroupTrainees([]);
                  setAvailableTrainees([]);
                  setSelectedTraineeIds([]);
                  setTraineeSearch('');
                }}
              >
                <Icon icon="fluent:dismiss-24-regular" width={24} />
              </button>
            </div>

            <div className="flex-1 overflow-hidden p-8 grid gap-8 md:grid-cols-2 bg-gray-50/30">
              {/* Current trainees */}
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/30 flex items-center justify-between">
                  <h6 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">In this Group ({groupTrainees.length})</h6>
                </div>
                <div className="flex-1 overflow-y-auto px-2 py-2">
                  {groupTraineesLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        <div className="w-8 h-8 border-3 border-gray-100 border-t-[#7083FF] rounded-full animate-spin mb-3" />
                        <p className="text-xs font-bold uppercase tracking-wider">Syncing members...</p>
                    </div>
                  ) : groupTrainees.length === 0 ? (
                    <div className="py-20 text-center flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                            <Icon icon="fluent:person-board-24-regular" className="text-gray-200 w-8 h-8" />
                        </div>
                        <p className="text-sm font-bold text-gray-400 max-w-[180px]">No members assigned to this group yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {groupTrainees.map((t) => (
                        <div key={t.id} className="p-3 rounded-2xl hover:bg-gray-50 transition-colors flex items-center justify-between gap-3 group/item">
                          <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-700 font-bold text-xs">
                                    {t.profile_pic_url ? <img src={t.profile_pic_url} className="w-full h-full object-cover rounded-xl" /> : (t.name?.[0] || 'U')}
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-gray-900 truncate">
                                      {t.name || t.full_name || 'Unnamed'}
                                    </p>
                                    <p className="text-[11px] text-gray-400 truncate">{t.email}</p>
                                </div>
                          </div>
                          <button
                            type="button"
                            className="w-8 h-8 rounded-lg bg-white border border-gray-100 text-gray-400 hover:text-red-600 hover:border-red-100 hover:shadow-sm transition-all flex items-center justify-center opacity-0 group-hover/item:opacity-100"
                            title="Remove Member"
                            onClick={() => handleRemoveTrainee(t)}
                          >
                            <Icon icon="fluent:dismiss-16-regular" width={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* In-Group Pagination */}
                {groupTraineeTotal > groupTraineeLimit && (
                    <div className="px-6 py-3 border-t border-gray-50 bg-gray-50/10 flex items-center justify-between">
                        <button 
                            disabled={groupTraineePage === 1 || groupTraineesLoading}
                            onClick={() => {
                                const next = groupTraineePage - 1;
                                setGroupTraineePage(next);
                                fetchGroupTrainees(manageGroup.id, next);
                            }}
                            className="p-1 rounded-lg hover:bg-white disabled:opacity-30"
                        >
                            <Icon icon="fluent:chevron-left-16-regular" />
                        </button>
                        <span className="text-[10px] font-bold text-gray-400">PAGE {groupTraineePage} / {Math.ceil(groupTraineeTotal / groupTraineeLimit)}</span>
                        <button 
                            disabled={groupTraineePage >= Math.ceil(groupTraineeTotal / groupTraineeLimit) || groupTraineesLoading}
                            onClick={() => {
                                const next = groupTraineePage + 1;
                                setGroupTraineePage(next);
                                fetchGroupTrainees(manageGroup.id, next);
                            }}
                            className="p-1 rounded-lg hover:bg-white disabled:opacity-30"
                        >
                            <Icon icon="fluent:chevron-right-16-regular" />
                        </button>
                    </div>
                )}
              </div>

              {/* Add trainees */}
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/30">
                  <h6 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Find Members to Add</h6>
                </div>
                
                <div className="p-4 border-b border-gray-50">
                    <div className="relative group/input">
                        <Icon
                          icon="fluent:search-20-regular"
                          width={18}
                          height={18}
                          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within/input:text-[#7083FF] transition-colors"
                        />
                        <input
                          type="text"
                          value={traineeSearch}
                          onChange={(e) => setTraineeSearch(e.target.value)}
                          onKeyUp={(e) => e.key === 'Enter' && fetchAvailableTrainees(traineeSearch)}
                          placeholder="Search by name or email"
                          className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-gray-50/50 border border-gray-100 text-sm font-medium focus:outline-none focus:ring-4 focus:ring-[#7083FF]/5 focus:border-[#7083FF]/30 transition-all"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-2 py-2">
                  {availableLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        <div className="w-8 h-8 border-3 border-gray-100 border-t-[#7083FF] rounded-full animate-spin mb-3" />
                        <p className="text-xs font-bold uppercase tracking-wider">Searching universe...</p>
                    </div>
                  ) : availableTrainees.length === 0 ? (
                    <div className="py-20 text-center flex flex-col items-center">
                        <Icon icon="fluent:search-square-24-regular" className="text-gray-100 w-12 h-12 mb-3" />
                        <p className="text-sm font-bold text-gray-400 px-10">No users match your criteria. Try another search.</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {availableTrainees.map((u) => (
                        <label key={u.id} className="p-3 rounded-2xl hover:bg-gray-50 transition-colors flex items-center gap-3 cursor-pointer group/label">
                            <div className="relative flex items-center">
                                <input
                                    type="checkbox"
                                    className="w-5 h-5 rounded-lg border-2 border-gray-200 text-[#7083FF] focus:ring-offset-0 focus:ring-transparent transition-all cursor-pointer checked:border-[#7083FF]"
                                    checked={selectedTraineeIds.includes(u.id)}
                                    onChange={() => toggleTraineeSelection(u.id)}
                                />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold text-gray-900 truncate">
                                  {u.name || u.full_name || 'Anonymous'}
                                </p>
                                <p className="text-[11px] text-gray-400 truncate">{u.email}</p>
                            </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Available Pagination */}
                {availableTotal > availableLimit && (
                    <div className="px-6 py-3 border-t border-gray-50 bg-gray-50/10 flex items-center justify-between">
                        <button 
                            disabled={availablePage === 1 || availableLoading}
                            onClick={() => {
                                const next = availablePage - 1;
                                setAvailablePage(next);
                                fetchAvailableTrainees(traineeSearch, next);
                            }}
                            className="p-1 rounded-lg hover:bg-white disabled:opacity-30"
                        >
                            <Icon icon="fluent:chevron-left-16-regular" />
                        </button>
                        <span className="text-[10px] font-bold text-gray-400">PAGE {availablePage} / {Math.ceil(availableTotal / availableLimit)}</span>
                        <button 
                            disabled={availablePage >= Math.ceil(availableTotal / availableLimit) || availableLoading}
                            onClick={() => {
                                const next = availablePage + 1;
                                setAvailablePage(next);
                                fetchAvailableTrainees(traineeSearch, next);
                            }}
                            className="p-1 rounded-lg hover:bg-white disabled:opacity-30"
                        >
                            <Icon icon="fluent:chevron-right-16-regular" />
                        </button>
                    </div>
                )}

                <div className="p-4 bg-gray-50 border-t border-gray-100">
                  <button
                    type="button"
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-[#7083FF] text-white text-sm font-bold shadow-xl shadow-[#7083FF]/20 hover:bg-[#5f73e6] transition-all disabled:opacity-30 disabled:shadow-none active:scale-[0.98]"
                    onClick={handleAddSelectedTrainees}
                    disabled={selectedTraineeIds.length === 0}
                  >
                    <Icon icon="fluent:person-add-24-regular" width={20} height={20} />
                    Add {selectedTraineeIds.length > 0 ? `${selectedTraineeIds.length} ` : ''}Selected to Group
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TraineeGroups;
